/** @typedef {(input: HTMLInputElement, quantity: number, stepperRoot: HTMLElement) => void} QuantityStepperChangeHandler */

export const DEFAULT_QUANTITY_MIN = 0;
export const DEFAULT_QUANTITY_MAX = 30;

/**
 * 数量入力値を整数として解釈する（空・不正は 0）。
 *
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
export function parseQuantityInputValue(value) {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }

    const parsed = Number.parseInt(String(value), 10);

    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * 数量を min〜max の範囲に収める。
 *
 * @param {number} quantity
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clampQuantityValue(quantity, min = DEFAULT_QUANTITY_MIN, max = DEFAULT_QUANTITY_MAX) {
    return Math.min(max, Math.max(min, quantity));
}

/**
 * data-min / data-max からステッパーの上下限を読む。
 *
 * @param {HTMLElement} stepperRoot
 * @returns {{ min: number, max: number }}
 */
export function readQuantityStepperBounds(stepperRoot) {
    const min = parseQuantityInputValue(stepperRoot.dataset.min ?? DEFAULT_QUANTITY_MIN);
    const max = parseQuantityInputValue(stepperRoot.dataset.max ?? DEFAULT_QUANTITY_MAX);

    return { min, max };
}

/**
 * 入力値の正規化と +/- ボタンの disabled を同期する。
 *
 * @param {HTMLElement} stepperRoot
 * @returns {number}
 */
export function syncQuantityStepperUi(stepperRoot) {
    const quantityInput = stepperRoot.querySelector('[data-quantity-stepper-input]');
    const decreaseButton = stepperRoot.querySelector('[data-quantity-stepper-decrease]');
    const increaseButton = stepperRoot.querySelector('[data-quantity-stepper-increase]');
    const { min, max } = readQuantityStepperBounds(stepperRoot);
    const quantity = clampQuantityValue(parseQuantityInputValue(quantityInput?.value), min, max);

    if (quantityInput) {
        quantityInput.value = String(quantity);
    }
    if (decreaseButton) {
        decreaseButton.disabled = quantity <= min;
    }
    if (increaseButton) {
        increaseButton.disabled = quantity >= max;
    }

    return quantity;
}

/**
 * キーボード・ペースト等による直接入力を抑止する（+/- ボタンのみで変更）。
 *
 * @param {HTMLInputElement} quantityInput
 * @returns {void}
 */
function blockQuantityDirectEditing(quantityInput) {
    quantityInput.readOnly = true;

    const preventEditing = (event) => {
        event.preventDefault();
    };

    quantityInput.addEventListener('keydown', preventEditing);
    quantityInput.addEventListener('keypress', preventEditing);
    quantityInput.addEventListener('paste', preventEditing);
    quantityInput.addEventListener('drop', preventEditing);
    quantityInput.addEventListener('wheel', preventEditing);
}

/**
 * 数量ステッパー（[-] input [+]）を初期化する。
 *
 * @param {ParentNode} root
 * @param {{ onChange?: QuantityStepperChangeHandler|null }} [options]
 * @returns {void}
 */
export function initQuantitySteppers(root = document, options = {}) {
    const onChange = options.onChange ?? null;

    root.querySelectorAll('[data-quantity-stepper]').forEach((stepperRoot) => {
        if (!(stepperRoot instanceof HTMLElement)) {
            return;
        }
        if (stepperRoot.dataset.quantityStepperBound === '1') {
            return;
        }
        stepperRoot.dataset.quantityStepperBound = '1';

        const quantityInput = stepperRoot.querySelector('[data-quantity-stepper-input]');
        const decreaseButton = stepperRoot.querySelector('[data-quantity-stepper-decrease]');
        const increaseButton = stepperRoot.querySelector('[data-quantity-stepper-increase]');
        const { min, max } = readQuantityStepperBounds(stepperRoot);

        if (quantityInput instanceof HTMLInputElement) {
            blockQuantityDirectEditing(quantityInput);
        }

        const notifyChange = () => {
            const quantity = syncQuantityStepperUi(stepperRoot);
            if (typeof onChange === 'function' && quantityInput instanceof HTMLInputElement) {
                onChange(quantityInput, quantity, stepperRoot);
            }
        };

        decreaseButton?.addEventListener('click', () => {
            const quantity = syncQuantityStepperUi(stepperRoot);
            if (quantity > min && quantityInput instanceof HTMLInputElement) {
                quantityInput.value = String(quantity - 1);
                notifyChange();
            }
        });

        increaseButton?.addEventListener('click', () => {
            const quantity = syncQuantityStepperUi(stepperRoot);
            if (quantity < max && quantityInput instanceof HTMLInputElement) {
                quantityInput.value = String(quantity + 1);
                notifyChange();
            }
        });

        notifyChange();
    });
}
